import React, { useMemo, useState } from 'react'
import classNames from 'classnames'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  LocationMarkerIcon,
  TruckIcon
} from '@heroicons/react/solid'
import startOfMonth from 'date-fns/startOfMonth'
import endOfMonth from 'date-fns/endOfMonth'
import startOfWeek from 'date-fns/startOfWeek'
import endOfWeek from 'date-fns/endOfWeek'
import isBefore from 'date-fns/isBefore'
import formatDate from 'date-fns/format'
import isSameMonth from 'date-fns/isSameMonth'
import subMonths from 'date-fns/subMonths'
import addDays from 'date-fns/addDays'
import addMonths from 'date-fns/addMonths'
import { LotScheduleQuery } from 'components/pages/LotSchedulePage/types/lotScheduleQuery'
import parseISO from 'date-fns/parseISO'
import Link from 'next/link'
import stripTimeZone from 'utils/stripTimeZone'

export type MonthDay = {
  date: string
  dateTime: Date
  isCurrentMonth: boolean
  isToday: boolean
  events: NonNullable<LotScheduleQuery['lot']>['locationSchedule']
}

const getDays = (
  month: Date,
  locationSchedule: NonNullable<LotScheduleQuery['lot']>['locationSchedule']
): MonthDay[] => {
  const days = []
  const start = startOfMonth(month)
  const end = endOfMonth(month)
  const startDate = startOfWeek(start)
  const endDate = endOfWeek(end)
  let currentDate = startDate

  while (isBefore(currentDate, endDate)) {
    const currentDateFormatted = formatDate(currentDate, 'yyyy-MM-dd')
    days.push({
      date: currentDateFormatted,
      dateTime: currentDate,
      isCurrentMonth: isSameMonth(currentDate, month),
      isToday: currentDateFormatted === formatDate(new Date(), 'yyyy-MM-dd'),
      events: locationSchedule.filter(
        (event) =>
          currentDateFormatted ===
          formatDate(parseISO(event.date), 'yyyy-MM-dd')
      )
    })
    currentDate = addDays(currentDate, 1)
  }
  return days
}

const formatTime = (time: Date): string => {
  return formatDate(time, 'h:mmaa').toLowerCase().replace(':00', '')
}

type Props = {
  isLotGroup: boolean
  locationSchedule:
    | NonNullable<LotScheduleQuery['lot']>['locationSchedule']
    | NonNullable<LotScheduleQuery['lotGroup']>['locationSchedule']
}

const EventsList = ({ locationSchedule, isLotGroup }: Props) => (
  <ol className="divide-y divide-gray-200 leading-6 lg:col-span-7 xl:col-span-8">
    {locationSchedule.map((calendarDay) => (
      <li
        key={calendarDay.id}
        className="relative flex space-x-6 py-6 xl:static"
      >
        <div className="flex-auto">
          <h3 className="pr-10 font-semibold text-gray-900 xl:pr-0">
            {calendarDay.dateAlias}
          </h3>
          <dl className="mt-2 flex flex-col text-gray-800 xl:flex-row">
            <dd className="flex flex-col gap-y-5 lg:gap-y-3 w-full">
              {calendarDay.locations.map((location) => (
                <div
                  key={location.id}
                  className="bg-gray-100 hover:bg-gray-200 transition-all duration-150 p-2 rounded-md cursor-pointer group w-full"
                >
                  <Link
                    href={location.customerUrl}
                    prefetch={false}
                    target="_blank"
                  >
                    <dl className="flex flex-wrap">
                      {isLotGroup && (
                        <div className="w-full flex items-center space-x-3">
                          <dt>
                            <span className="sr-only">Location</span>
                            <LocationMarkerIcon
                              aria-hidden="true"
                              className="h-5 w-5 text-gray-400"
                            />
                          </dt>
                          <dd className="group-hover:text-gray-900">
                            {location.lot.nameWithinGroup}
                          </dd>
                        </div>
                      )}

                      <div className="flex items-center space-x-3 lg:w-auto w-full lg:mr-3.5 lg:border-r lg:border-gray-400 lg:border-opacity-50 lg:pr-3.5">
                        <dt>
                          <span className="sr-only">Time</span>
                          <ClockIcon
                            aria-hidden="true"
                            className="h-5 w-5 text-gray-400"
                          />
                        </dt>
                        <dd>
                          {formatTime(
                            parseISO(stripTimeZone(location.startTime))
                          )}{' '}
                          -{' '}
                          {formatTime(
                            parseISO(stripTimeZone(location.endTime))
                          )}
                        </dd>
                      </div>
                      <div className="flex items-center space-x-3">
                        <dt>
                          <span className="sr-only">Truck name</span>
                          <TruckIcon
                            aria-hidden="true"
                            className="h-5 w-5 text-gray-400"
                          />
                        </dt>
                        <dd className="underline group-hover:text-gray-900">
                          {location.truck.name}
                        </dd>
                      </div>
                    </dl>
                  </Link>
                </div>
              ))}
            </dd>
          </dl>
        </div>
      </li>
    ))}
  </ol>
)

const Calendar = ({ locationSchedule, isLotGroup }: Props) => {
  const [month, setMonth] = useState(
    locationSchedule[0] ? parseISO(locationSchedule[0].date) : new Date()
  )
  const days = useMemo(() => getDays(month, locationSchedule), [
    month,
    locationSchedule
  ])

  return (
    <div>
      <div className="lg:grid lg:grid-cols-5">
        {locationSchedule.length === 0 && (
          <div className="lg:col-span-2 relative flex justify-center items-center">
            <TruckIcon className="w-40 h-40 text-orange-50" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 flex flex-col justify-center items-center">
              <div className="italic text-gray-500 font-extralight text-2xl text-center">
                No trucks on this location
              </div>
            </div>
          </div>
        )}
        {locationSchedule.length > 0 && (
          <div className="lg:col-span-2 pt-4 pr-10">
            <EventsList
              isLotGroup={isLotGroup}
              locationSchedule={locationSchedule}
            />
          </div>
        )}
        <div className="hidden lg:flex lg:col-span-3 lg:flex-col">
          <div>
            <header className="flex items-center justify-between border-b border-gray-200 py-4 lg:flex-none">
              <h1 className="text-lg font-semibold text-gray-900">
                <time dateTime="2022-01">{formatDate(month, 'MMMM yyyy')}</time>
              </h1>
              <div className="flex items-center">
                <div className="flex items-center rounded-md shadow-sm md:items-stretch border border-solid border-gray-300">
                  <button
                    className="flex items-center justify-center rounded-l-md py-2 pl-3 pr-4 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:px-2 bg-white cursor-pointer hover:bg-gray-50"
                    onClick={() => setMonth(subMonths(month, 1))}
                    type="button"
                  >
                    <span className="sr-only">Previous month</span>
                    <ChevronLeftIcon aria-hidden="true" className="h-5 w-5" />
                  </button>
                  <button
                    className="hidden px-3.5 text-sm font-medium hover:text-gray-900 focus:relative md:block bg-white cursor-pointer hover:bg-gray-50 text-gray-700"
                    onClick={() => setMonth(new Date())}
                    type="button"
                  >
                    Today
                  </button>
                  <span className="md:hidden relative -mx-px h-5 w-px bg-gray-300" />
                  <button
                    className="flex items-center justify-center rounded-r-md py-2 pl-4 pr-3 text-gray-400 hover:text-gray-500 focus:reltive md:w-9 md:px-2 bg-white cursor-pointer hover:bg-gray-50"
                    onClick={() => setMonth(addMonths(month, 1))}
                    type="button"
                  >
                    <span className="sr-only">Next month</span>
                    <ChevronRightIcon aria-hidden="true" className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </header>
            <div className="shadow ring-1 ring-black ring-opacity-5 lg:flex lg:flex-auto lg:flex-col">
              <div className="grid grid-cols-7 gap-px border-b border-gray-300 bg-gray-200 text-center text-xs font-semibold leading-6 text-gray-700 lg:flex-none">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(
                  (day) => (
                    <div key={day} className="bg-white py-2">
                      {day.substr(0, 1)}
                      <span className="sr-only sm:not-sr-only">
                        {day.substr(1, 2)}
                      </span>
                    </div>
                  )
                )}
              </div>
              <div className="flex bg-gray-200 text-xs leading-6 text-gray-700 lg:flex-auto">
                <div
                  className={classNames(
                    'isolate grid w-full grid-cols-7 gap-px',
                    {
                      'grid-rows-6': days.length / 7 === 6,
                      'grid-rows-5': days.length / 7 === 5
                    }
                  )}
                >
                  {days.map((day) => (
                    <div
                      key={day.date}
                      className={classNames(
                        day.isCurrentMonth
                          ? 'bg-white'
                          : 'bg-gray-50 text-gray-700',
                        'relative py-1 px-2 hover:bg-gray-100 overflow-hidden'
                      )}
                    >
                      <time
                        className={
                          day.isToday ? 'text-orange-500 font-bold' : undefined
                        }
                        dateTime={day.date}
                      >
                        {(day.date ?? '').split('-')!.pop()!.replace(/^0/, '')}
                      </time>
                      <ol className="mt-2 p-0">
                        {day.events.length === 0 && (
                          <li className="list-none invisible">
                            <span>
                              <p className="flex-auto truncate font-medium text-gray-900 m-0 font-sans">
                                placeholder
                              </p>
                            </span>
                          </li>
                        )}
                        {day.events.map((calendarDay) => (
                          <li
                            key={calendarDay.id}
                            className="list-none flex flex-col space-y-1"
                          >
                            {calendarDay.locations.map((location) => (
                              <Link
                                key={location.id}
                                href={location.customerUrl}
                                prefetch={false}
                                target="_blank"
                              >
                                <div className="text-xs px-1 py-0.5 bg-orange-300 rounded-md cursor-pointer">
                                  <span>{location.truck.name}</span>
                                </div>
                              </Link>
                            ))}
                          </li>
                        ))}
                      </ol>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Calendar
